import React from "react";
import {useNavigate} from "react-router";
import {Button, Link, Tooltip} from "@mui/material";
import {RocketLaunchOutlined} from "@mui/icons-material";
import {notificationStore} from "~/stores/init";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {
    PtV2Action,
    PtV2CreateInitiativeResult,
    PtV2PlanItem,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {
    buildInitiativeDraft,
    initiativeBlockedReason,
    initiativeLink,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Initiative.ts";
import {usePtV2InitiativeOptionsQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2InitiativeDialog} from "~/experiments/pages/payment-terms-v2/components/PtV2InitiativeDialog.tsx";

type Props = {
    item: PtV2PlanItem;
    /** The stored action for `item.key`, if any; carries the initiative once one exists. */
    existing: PtV2Action | undefined;
    /** `contained` in the drawer footer, `text` on an action-plan row. */
    variant?: 'contained' | 'text';
};

/** "Create savings initiative", or "Initiative #id · Open" once the item has one. */
export const PtV2InitiativeControl: React.FC<Props> = ({item, existing, variant = 'text'}) => {
    const {tabPath} = usePtV2Context();
    const navigate = useNavigate();
    const optionsQ = usePtV2InitiativeOptionsQuery();
    const [open, setOpen] = React.useState(false);

    const go = (link: {href: string; external: boolean}) => {
        if (link.external) window.open(link.href, '_blank', 'noopener');
        else void navigate(link.href);
    };

    if (existing?.initiativeId != null) {
        const link = initiativeLink(existing, window.location.origin);
        return <span className="ptv2-initiative ptv2-num" title={existing.initiativeTitle ?? undefined}>
            Initiative #{existing.initiativeId}
            {link && <> · <Link component="button" type="button" underline="hover" onClick={() => go(link)}>Open</Link></>}
        </span>;
    }

    const blocked = optionsQ.isError
        ? 'The Savings Initiative Tracker could not be reached.'
        : initiativeBlockedReason(optionsQ.data);
    const pageUrl = window.location.origin + tabPath(item.source?.tab ?? 'actions');

    const onCreated = (result: PtV2CreateInitiativeResult) => {
        setOpen(false);
        const link = initiativeLink(result, window.location.origin);
        notificationStore.show(
            result.duplicate
                ? `This item already has initiative #${result.initiativeId}.`
                : `Savings initiative #${result.initiativeId} created.`,
            {
                id: `ptv2-initiative-${result.id}`,
                severity: result.duplicate ? 'info' : 'success',
                autoHideDuration: 8000,
                actionText: 'Open initiative',
                onAction: link ? () => go(link) : undefined,
            },
        );
    };

    return <>
        <Tooltip title={blocked ?? ''}>
            <span>
                <Button size="small" variant={variant} startIcon={<RocketLaunchOutlined/>}
                        disabled={blocked != null} onClick={() => setOpen(true)}>
                    Create savings initiative
                </Button>
            </span>
        </Tooltip>
        {open && optionsQ.data && <PtV2InitiativeDialog
            item={item}
            existing={existing}
            draft={buildInitiativeDraft(item, item.source, pageUrl)}
            options={optionsQ.data}
            onCreated={onCreated}
            onClose={() => setOpen(false)}/>}
    </>;
};
